import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import EntityNotificationSummarySchema from "notifications/entity-summaries/_schemas/entity-notification-summary-schema";
import NotificationBatchSchema from "notifications/_schemas/notification-batch-schema";
import { entityNotificationsListTag, notificationsApi } from "notifications/_api/notifications-api";

/**
 * Keeps the entity-wide notifications feed live (GitHub issue #4438).
 *
 * Three things about this are deliberate:
 *
 * - It only ever invalidates the entity feed's own RTK Query tag, so an entity-wide event never
 *   touches anyone's personal inbox cache, and a payload the client cannot parse simply produces
 *   no refetch rather than a half-populated row.
 * - Whether a browser may attach to the channel at all is decided server-side, by the Ably token
 *   the API mints: `AblyAuthController` puts this channel in a user's capabilities only while they
 *   hold an entity-wide notifications grant. A withdrawn grant therefore stops delivery at the next
 *   token refresh; this file is not the gate and must not be treated as one. The session does carry
 *   the same grant as `hasEntityWideNotificationsAccess`, and `ablyChannelNamesForSession` resolves
 *   no channel name without it, so a session that cannot attach never asks — that is noise
 *   avoidance, not enforcement.
 * - It registers no connection-health entry and does not start the shared Ably connection, so a
 *   user without the grant neither forces a connection nor raises a system banner when the attach
 *   is refused.
 */
const callbacks = withCallbacks()
  .add("notification-received", (payload: unknown) => (dispatch) => {
    if (!EntityNotificationSummarySchema.isValidJSON(payload)) return;
    dispatch(notificationsApi.util.invalidateTags([entityNotificationsListTag]));
  })
  .add("notification-updated", (payload: unknown) => (dispatch) => {
    if (!EntityNotificationSummarySchema.isValidJSON(payload)) return;
    dispatch(notificationsApi.util.invalidateTags([entityNotificationsListTag]));
  })
  /**
   * One bulk change, one refetch (GitHub issue #2963). A bulk mark-seen used to arrive here as one
   * `notification-updated` per notification, and each one invalidated the entity feed's tag —
   * refetching the whole feed once per row that changed.
   */
  .add("notifications-bulk-updated", (payload: unknown) => (dispatch) => {
    if (!NotificationBatchSchema.isValidJSON(payload)) return;
    if (!payload.notifications.some((summary) => EntityNotificationSummarySchema.isValidJSON(summary))) return;
    dispatch(notificationsApi.util.invalidateTags([entityNotificationsListTag]));
  })
  .add("notification-deleted", (payload: unknown) => (dispatch) => {
    if (!payload || typeof payload !== "object" || !("id" in payload)) return;
    dispatch(notificationsApi.util.invalidateTags([entityNotificationsListTag]));
  });

export const entityNotificationsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.entityNotifications,
  shouldConnectionStartImmediately: false,
});
